import i18n from '@/i18n';
import { displayShopFact } from '@/i18n/catalogDisplayText';
import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  buildStoreBasicRows,
  buildStoreQuotaRows,
  getStoreDisplayName,
  getStoreStatusMeta,
  getStoreSupplierId,
  pickStoreInfoNode,
  pickStoreQuotaNode,
} from '@/shein-platform/utils/sheinStoreInfo';
import ShopPublishPermissionPanel from '@/shein-platform/modules/shop/ShopPublishPermissionPanel';
import ShopSiteListPanel from '@/shein-platform/modules/shop/ShopSiteListPanel';
import ShopBrandIpPanel from '@/shein-platform/modules/shop/ShopBrandIpPanel';
import ShopCategoryPanel from '@/shein-platform/modules/shop/ShopCategoryPanel';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import SheinRefreshButton from '@/shein-platform/components/SheinRefreshButton';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import SheinShopAvatar from '@/shein-platform/components/SheinShopAvatar';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinManualSyncQuota } from '@/shein-platform/hooks/useSheinManualSyncQuota';
import PlatformShopDimensionTabs from '@/components/platform/PlatformShopDimensionTabs';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import { imageStorageAPI } from '@/utils/api';
import { pickUrlFromImageStorageResponse } from '@/utils/imageUrlUtils';
import '@/styles/shein-platform/shein-product-ops.css';

const SHOP_PROFILE_SYNC_KINDS = ['shopProfile'];
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

const SYNC_SECTION_LABELS = {
  storeInfo: '基本信息',
  publishPermission: '发品资格',
  siteList: '售卖站点',
  brands: '品牌',
  ips: 'IP',
  categories: '可发类目',
};

function KvTable({ rows }) {
  if (!rows.length) {
    return <p className="spo-return-detail__empty">{fixedUi("暂无数据")}</p>;
  }
  return (
    <table className="spo-return-detail__kv">
      <tbody>
        {rows.map((row) => (
          <tr key={row.key || row.label}>
            <th scope="row">{displayShopFact(row.label)}</th>
            <td>{displayShopFact(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ShopInfoPage() {
  const {
    activeShop,
    loading,
    activeShopId,
    scopedShops = [],
    isAllShopsMode,
    patchLocalShop,
  } = useSheinShop();
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();
  const [pageShopScope, setPageShopScope] = useState(() => activeShopId || '');
  const [shopPickOpen, setShopPickOpen] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const [fetchError, setFetchError] = useState('');
  const [publishPermission, setPublishPermission] = useState(null);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [siteList, setSiteList] = useState(null);
  const [siteListLoading, setSiteListLoading] = useState(false);
  const [siteListError, setSiteListError] = useState('');
  const [brands, setBrands] = useState(null);
  const [ips, setIps] = useState(null);
  const [brandIpLoading, setBrandIpLoading] = useState(false);
  const [brandIpError, setBrandIpError] = useState('');
  const [categories, setCategories] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState('');
  const prevShopIdRef = useRef(null);
  const pendingSyncRef = useRef(false);

  useEffect(() => {
    setPageShopScope(activeShopId || '');
  }, [activeShopId]);

  const showOverview = isAllShopsScope(pageShopScope)
    || (isAllShopsMode && !isConcreteShopId(pageShopScope));

  const viewShopId = useMemo(() => {
    if (isConcreteShopId(pageShopScope)) return String(pageShopScope);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    return '';
  }, [pageShopScope, activeShopId]);

  const viewShop = useMemo(() => {
    if (!viewShopId) return null;
    return (scopedShops || []).find((s) => String(s.id) === String(viewShopId))
      || (activeShop && String(activeShop.id) === String(viewShopId) ? activeShop : null);
  }, [viewShopId, scopedShops, activeShop]);

  const loadPublishPermission = useCallback(async () => {
    if (!viewShopId) {
      setPublishPermission(null);
      setPermissionError('');
      return null;
    }
    setPermissionLoading(true);
    setPermissionError('');
    try {
      const res = await sheinPlatformAPI.getShopPublishPermission(viewShopId);
      setPublishPermission(res ?? null);
      return res;
    } catch (e) {
      setPublishPermission(null);
      setPermissionError(e?.message || '读取发品资格缓存失败');
      return null;
    } finally {
      setPermissionLoading(false);
    }
  }, [viewShopId]);

  const loadSiteList = useCallback(async () => {
    if (!viewShopId) {
      setSiteList(null);
      setSiteListError('');
      return null;
    }
    setSiteListLoading(true);
    setSiteListError('');
    try {
      const res = await sheinPlatformAPI.getShopSiteList(viewShopId);
      setSiteList(res?.siteList ?? null);
      return res;
    } catch (e) {
      setSiteList(null);
      setSiteListError(e?.message || '读取售卖站点缓存失败');
      return null;
    } finally {
      setSiteListLoading(false);
    }
  }, [viewShopId]);

  const loadBrandIp = useCallback(async () => {
    if (!viewShopId) {
      setBrands(null);
      setIps(null);
      setBrandIpError('');
      return null;
    }
    setBrandIpLoading(true);
    setBrandIpError('');
    try {
      const [brandRes, ipRes] = await Promise.all([
        sheinPlatformAPI.getShopBrands(viewShopId),
        sheinPlatformAPI.getShopIps(viewShopId),
      ]);
      setBrands(brandRes?.brands ?? null);
      setIps(ipRes?.ips ?? null);
      return { brandRes, ipRes };
    } catch (e) {
      setBrands(null);
      setIps(null);
      setBrandIpError(e?.message || '读取品牌与 IP 缓存失败');
      return null;
    } finally {
      setBrandIpLoading(false);
    }
  }, [viewShopId]);

  const loadCategories = useCallback(async () => {
    if (!viewShopId) {
      setCategories(null);
      setCategoryError('');
      return null;
    }
    setCategoryLoading(true);
    setCategoryError('');
    try {
      const res = await sheinPlatformAPI.getShopCategories(viewShopId);
      setCategories(res?.categories ?? null);
      return res;
    } catch (e) {
      setCategories(null);
      setCategoryError(e?.message || '读取商品分类缓存失败');
      return null;
    } finally {
      setCategoryLoading(false);
    }
  }, [viewShopId]);

  const loadStoreInfo = useCallback(async () => {
    if (!viewShopId) {
      setStoreInfo(null);
      return null;
    }
    setFetching(true);
    setFetchError('');
    try {
      const res = await sheinPlatformAPI.getShopStoreInfo(viewShopId);
      setStoreInfo(res?.storeInfo ?? null);
      return res;
    } catch (e) {
      setStoreInfo(null);
      setFetchError(e?.message || '读取店铺信息缓存失败');
      return null;
    } finally {
      setFetching(false);
    }
  }, [viewShopId]);

  const loadFromCache = useCallback(async () => {
    if (!viewShopId) {
      setLastRefreshedAt('');
      return;
    }
    const [storeRes, permRes, siteRes] = await Promise.all([
      loadStoreInfo(),
      loadPublishPermission(),
      loadSiteList(),
      loadBrandIp(),
      loadCategories(),
    ]);
    const syncedAt = storeRes?.syncedAt
      || permRes?.syncedAt
      || siteRes?.syncedAt
      || '';
    setLastRefreshedAt(syncedAt);
  }, [viewShopId, loadStoreInfo, loadPublishPermission, loadSiteList, loadBrandIp, loadCategories]);

  const syncFromSheinAction = useCallback(async () => {
    if (!viewShopId) {
      setLastRefreshedAt('');
      return;
    }
    setFetching(true);
    setPermissionLoading(true);
    setSiteListLoading(true);
    setBrandIpLoading(true);
    setCategoryLoading(true);
    setFetchError('');
    setPermissionError('');
    setSiteListError('');
    setBrandIpError('');
    setCategoryError('');
    try {
      const track = await startJob({
        kind: 'shopProfile',
        shopId: viewShopId,
        get label() { return fixedUi("同步店铺信息"); },
        silent: true,
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      await loadFromCache();
      if (!result?.ok) return;
      const errors = result?.view?.errors && typeof result.view.errors === 'object'
        ? result.view.errors
        : {};
      setFetchError(errors.storeInfo || '');
      setPermissionError(errors.publishPermission || '');
      setSiteListError(errors.siteList || '');
      setBrandIpError(errors.brands || errors.ips || '');
      setCategoryError(errors.categories || '');
      const failedKeys = Object.keys(errors).filter((k) => errors[k]);
      if (failedKeys.length > 0) {
        const firstMsg = errors[failedKeys[0]];
        const labels = failedKeys.map((k) => SYNC_SECTION_LABELS[k] || k).join('、');
        showToast(
          failedKeys.length === 1
            ? firstMsg
            : `部分同步失败（${labels}）：${firstMsg}`,
          'warning',
        );
      } else {
        showToast(fixedUi("店铺信息同步成功"), 'success');
      }
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      try {
        await loadFromCache();
      } catch (_) {
        /* ignore */
      }
    } finally {
      setFetching(false);
      setPermissionLoading(false);
      setSiteListLoading(false);
      setBrandIpLoading(false);
      setCategoryLoading(false);
    }
  }, [viewShopId, loadFromCache, startJob, onTrackDone]);

  const {
    refresh: handleRefreshAll,
    refreshing: syncingLocal,
    cooldownSeconds: refreshAllCooldown,
  } = useGuardedRefresh(syncFromSheinAction);

  const quota = useSheinManualSyncQuota('shop-profile');
  const handleRefreshClick = () => {
    if (!quota.assertCanSync()) return;
    handleRefreshAll();
  };

  const handleAvatarPick = () => {
    if (uploadingAvatar || !viewShopId) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !viewShopId) return;
    if (!file.type.startsWith('image/')) {
      showToast(fixedUi("请选择图片文件"), 'warning');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      showToast(fixedUi("头像图片不能超过 5MB"), 'warning');
      return;
    }
    setUploadingAvatar(true);
    try {
      const uploaded = await imageStorageAPI.save(file, { folder: 'image-space', registerImageSpace: false });
      const url = pickUrlFromImageStorageResponse(uploaded);
      if (!url) throw new Error('上传失败');
      await sheinPlatformAPI.patchShopAvatar(viewShopId, url);
      patchLocalShop?.(viewShopId, { avatarUrl: url });
      showToast(fixedUi("店铺头像已更新"), 'success');
    } catch (err) {
      showToast(err?.message || '头像上传失败', 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    if (showOverview || !viewShopId) {
      prevShopIdRef.current = null;
      if (!viewShopId) setLastRefreshedAt('');
      return;
    }

    const prevShopId = prevShopIdRef.current;
    prevShopIdRef.current = viewShopId;
    const shouldSync = pendingSyncRef.current;
    pendingSyncRef.current = false;

    if (shouldSync) {
      syncFromSheinAction();
      return;
    }

    if (prevShopId == null || String(prevShopId) !== String(viewShopId)) {
      loadFromCache();
    }
  }, [showOverview, viewShopId, loadFromCache, syncFromSheinAction]);

  const syncBusy = isBusy('shopProfile', viewShopId);
  const isRefreshing = syncingLocal
    || syncBusy
    || fetching
    || permissionLoading
    || siteListLoading
    || brandIpLoading
    || categoryLoading;

  const shopFallbackName = viewShop?.shopName || '';
  const displayName = useMemo(
    () => getStoreDisplayName(storeInfo, shopFallbackName),
    [storeInfo, shopFallbackName],
  );
  const supplierId = useMemo(() => getStoreSupplierId(storeInfo), [storeInfo]);
  const statusMeta = useMemo(() => getStoreStatusMeta(storeInfo), [storeInfo]);
  const basicRows = useMemo(() => buildStoreBasicRows(storeInfo), [storeInfo]);
  const quotaRows = useMemo(() => buildStoreQuotaRows(storeInfo), [storeInfo]);
  const hasBasicInfo = Boolean(pickStoreInfoNode(storeInfo));
  const hasQuotaInfo = Boolean(pickStoreQuotaNode(storeInfo));

  const syncedMetaText = lastRefreshedAt
    ? `${fixedUi('最近同步')}：${formatSiteListSyncedAt(lastRefreshedAt)}`
    : (isRefreshing ? fixedUi('正在从 SHEIN 同步…') : fixedUi('暂无缓存，请点击「同步店铺信息」从 SHEIN 同步'));

  // 仅门禁选「全部」进入时展示；单店登录锁定当前店
  const shopDimensionTabs = isAllShopsMode && scopedShops.length >= 2 ? (
    <PlatformShopDimensionTabs
      shops={scopedShops}
      value={pageShopScope || PLATFORM_ALL_SHOPS_ID}
      onChange={setPageShopScope}
      showAll
    />
  ) : null;

  if (loading) {
    return (
      <div className="spo-return-detail spo-shop-info" data-testid="shein-shop-info">
        <div className="spo-return-detail__loading">{fixedUi("加载店铺信息…")}</div>
      </div>
    );
  }

  if (showOverview) {
    return (
      <div className="spo-return-detail spo-shop-info" data-testid="shein-shop-info">
        <header className="spo-return-detail__header">
          <div className="spo-return-detail__title-block">
            <h1 className="spo-return-detail__title">{fixedUi("店铺信息")}</h1>

          </div>
          <div className="spo-return-detail__actions">
            <SheinRefreshButton
              onClick={() => setShopPickOpen(true)}
              size="sm"
              className="spo-btn spo-btn--primary spo-btn--sm"
            >
              {fixedUi("同步店铺信息")}
            </SheinRefreshButton>
          </div>
        </header>
        {shopDimensionTabs}
        <div className="spo-shop-info__overview">
          <div className="spo-shop-info__table-wrap">
            <table className="spo-shop-info__table spo-shop-info__overview-table">
              <thead>
                <tr>
                  <th scope="col">{fixedUi("店铺名称")}</th>
                  <th scope="col">{fixedUi("店铺 ID")}</th>
                  <th scope="col">{fixedUi("授权状态")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {(scopedShops || []).map((shop) => (
                  <tr key={shop.id}>
                    <td>{shop.shopName || `店铺 ${shop.id}`}</td>
                    <td>{shop.id}</td>
                    <td>{fixedUi("已授权")}</td>
                    <td>
                      <button
                        type="button"
                        className="spo-btn spo-btn--ghost spo-btn--sm"
                        onClick={() => setPageShopScope(String(shop.id))}
                      >
                        查看详情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <PlatformShopPickModal
          open={shopPickOpen}
          shops={scopedShops}
          title={fixedUi("选择要同步的店铺")}
          description={fixedUi("全部视图下请选择一家店铺后再同步店铺信息。")}
          confirmText="查看并同步"
          onClose={() => setShopPickOpen(false)}
          onConfirm={(shopId) => {
            setShopPickOpen(false);
            pendingSyncRef.current = true;
            setPageShopScope(String(shopId));
          }}
        />
      </div>
    );
  }

  if (!viewShop) {
    return (
      <div className="spo-return-detail spo-shop-info" data-testid="shein-shop-info">
        <header className="spo-return-detail__header">
          <div className="spo-return-detail__title-block">
            <h1 className="spo-return-detail__title">{fixedUi("店铺信息")}</h1>
          </div>
        </header>
        {shopDimensionTabs}
        <div className="spo-return-detail__loading">
          {fixedUi("尚未配置 SHEIN 店铺。")}
          {' '}
          <Link to={`${IPE_WORKBENCH_PATH}?activeNav=platformShops`} className="spo-return-detail__crumb-link">
            {fixedUi("去平台模板 · 店铺管理")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SheinSyncProgressSlot kinds={SHOP_PROFILE_SYNC_KINDS} />
      <div className="spo-return-detail spo-shop-info" data-testid="shein-shop-info">
        {shopDimensionTabs}
        <header className="spo-return-detail__header">
          <div className="spo-shop-info__identity">
            <button
              type="button"
              className="spo-shop-info__avatar-btn"
              onClick={handleAvatarPick}
              disabled={uploadingAvatar}
              aria-label={fixedUi("更换店铺头像")}
              title={fixedUi("点击上传自定义头像")}
            >
              <SheinShopAvatar
                className="spo-shop-info__avatar"
                avatarUrl={viewShop?.avatarUrl}
                size={72}
              />
              <span className="spo-shop-info__avatar-overlay" aria-hidden="true">
                {uploadingAvatar ? fixedUi("上传中") : fixedUi("更换")}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="spo-shop-info__avatar-input"
              onChange={handleAvatarChange}
            />
            <div className="spo-return-detail__title-block">
              <h1 className="spo-return-detail__title">{fixedUi("店铺信息")}</h1>
              <div className="spo-return-detail__meta">
                <strong className="spo-return-detail__no">{displayName}</strong>
                {supplierId ? (
                  <span className="spo-shop-info__meta-id">{fixedUi("商家 ID")} {supplierId}</span>
                ) : null}
                {hasBasicInfo || statusMeta.label !== '—' ? (
                  <span className={`spo-records__status spo-records__status--${statusMeta.modifier}`}>
                    {displayShopFact(statusMeta.label)}
                  </span>
                ) : null}
              </div>
              <p className="spo-shop-info__synced">{syncedMetaText}</p>
              <p className="spo-shop-info__avatar-tip">
                {fixedUi("点击头像可自定义；未设置时顶栏默认显示 SHEIN")}
              </p>
            </div>
          </div>
          <div className="spo-return-detail__actions">
            {quota.remaining != null ? (
              <span
                className="spo-sync-quota"
                title={i18n.t('tiktokList.quotaTitle', { ns: 'workbench', limit: quota.limit })}
              >
                {i18n.t('tiktokList.quotaRemain', { ns: 'workbench', remaining: quota.remaining, limit: quota.limit })}
              </span>
            ) : null}
            <SheinRefreshButton
              onClick={handleRefreshClick}
              loading={isRefreshing}
              cooldownSeconds={refreshAllCooldown}
              busyLabel={fixedUi("同步中…")}
              size="sm"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={quota.exhausted}
            >
              {quota.exhausted ? i18n.t('tiktokList.quotaExhausted', { ns: 'workbench' }) : fixedUi("同步店铺信息")}
            </SheinRefreshButton>
          </div>
        </header>

        {fetchError ? (
          <div className="spo-ops-error" role="alert">{fetchError}</div>
        ) : null}

        {fetching && !storeInfo && !syncBusy ? (
          <div className="spo-return-detail__loading">{fixedUi("正在查询店铺信息…")}</div>
        ) : null}

        <div className="spo-return-detail__body">
          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{fixedUi("基本信息")}</h2>
            {!fetching && !fetchError && !hasBasicInfo ? (
              <p className="spo-return-detail__empty">{fixedUi("暂无店铺基础信息，请点击「同步店铺信息」同步。")}</p>
            ) : (
              <KvTable rows={basicRows} />
            )}
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{fixedUi("发品可用额度")}</h2>
            {!fetching && !hasQuotaInfo ? (
              <p className="spo-return-detail__empty">{fixedUi("暂无额度数据，请同步后查看。")}</p>
            ) : (
              <KvTable rows={quotaRows} />
            )}
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{fixedUi("发品资格")}</h2>
            <ShopPublishPermissionPanel
              loading={permissionLoading}
              error={permissionError}
              canPublishProduct={publishPermission?.canPublishProduct}
              reason={publishPermission?.reason}
            />
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{fixedUi("售卖站点")}</h2>
            <ShopSiteListPanel
              loading={siteListLoading}
              error={siteListError}
              siteList={siteList}
            />
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{fixedUi("品牌与 IP")}</h2>
            <ShopBrandIpPanel
              loading={brandIpLoading}
              error={brandIpError}
              brands={brands}
              ips={ips}
            />
          </section>

          <section className="spo-return-detail__section spo-shop-info__section--categories">
            <h2 className="spo-return-detail__section-title">{fixedUi("可发类目")}</h2>
            <ShopCategoryPanel
              loading={categoryLoading}
              error={categoryError}
              categories={categories}
            />
          </section>
        </div>
      </div>
    </>
  );
}
